import { useEffect, useState } from "react";
import { ApiRequestError, getExtensionStores } from "../lib/client";
import type { ExtensionStoreSummary } from "../lib/types";

type State =
  | { kind: "loading" }
  | { kind: "ready"; stores: ExtensionStoreSummary[] }
  | { kind: "error"; message: string };

/**
 * The directory of per-user Turso databases the browser extension owns (ADR-26).
 *
 * There is one database for each user in each subscriber they log in to. The extension reads
 * and writes it directly, and nothing on the server reads it. So this page is how support finds
 * one user's data: it gives the database name and a link into the Turso console, which has its
 * own login. No token appears here, and none is available from here.
 *
 * A subscriber is optional. With none selected the page lists every database, which is what
 * "find this user, whatever org they are in" needs.
 */
export function ExtensionStores({
  subscriberId,
}: {
  /** Narrow the listing to one org. Empty lists every database. */
  subscriberId: string;
}) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    getExtensionStores(subscriberId || undefined)
      .then((stores) => {
        if (!cancelled) setState({ kind: "ready", stores });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          kind: "error",
          message:
            err instanceof ApiRequestError
              ? err.message
              : "Failed to load extension stores.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [subscriberId]);

  if (state.kind === "loading") {
    return <div className="panel">Loading extension stores…</div>;
  }
  if (state.kind === "error") {
    return (
      <div className="panel">
        <span className="hint error">{state.message}</span>
      </div>
    );
  }

  const needle = filter.trim().toLowerCase();
  const rows = needle
    ? state.stores.filter(
        (database) =>
          database.userEmail?.toLowerCase().includes(needle) ||
          database.name.toLowerCase().includes(needle),
      )
    : state.stores;

  return (
    <div className="panel">
      <div className="toolbar">
        <input
          className="text-input grow"
          placeholder="Filter by email or database name…"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <span className="muted">
          {rows.length} of {state.stores.length}
        </span>
      </div>

      {state.stores.length === 0 ? (
        <span className="hint">
          No user has opened an extension store{" "}
          {subscriberId ? "in this org " : ""}yet.
        </span>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Database</th>
                <th>Created</th>
                <th>Console</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((database) => (
                <tr key={database.name}>
                  <td>{database.userEmail ?? database.userId}</td>
                  <td>
                    <code>{database.name}</code>
                  </td>
                  <td>
                    {database.createdAt
                      ? new Date(database.createdAt).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <a
                      href={database.consoleUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in Turso
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
